// The Transformation Snapshot's pure part (SPECIFICATION_TRANSFORMATION_SURVEYS
// 011126 V1.0.md, slice 1): the eight answers and how each is stored, the
// observation window, and the short report. Nothing here reads a database.
//
// 1-5 means HOW CONSISTENTLY something happened in the last four weeks. It is
// never maturity and never PMO's 0-3, and the report shows no overall score:
// there is no mean, percentage or level anywhere in this file.

import { shareOfScale } from "./share.ts";
import type { Applicability } from "./types";

export const SNAPSHOT_SLUG = "transformation-snapshot";

/** Below this many scored questions the profile is partial and nothing is ranked. */
export const MIN_SCORED_TO_RANK = 8;

/** Up to this many areas to investigate. */
export const MAX_PRIORITIES = 3;

/** The three unscored answers, in the order shown, with how each is stored. */
export const UNSCORED = [
  { id: "not_known", label: "Not known", applicability: "not_assessed" as const, rationale: null },
  { id: "not_applicable", label: "Not applicable", applicability: "not_applicable", rationale: "Not applicable" },
  {
    id: "no_opportunity",
    label: "No relevant opportunity in this period",
    applicability: "not_applicable",
    rationale: "No relevant opportunity",
  },
] as const;

export type UnscoredId = (typeof UNSCORED)[number]["id"];
export type SnapshotAnswer = { score: number } | UnscoredId;

export interface StoredAnswer {
  applicability: Applicability;
  score: number | null;
  rationale: string | null;
}

/** What one tap writes. */
export function patchFor(answer: SnapshotAnswer): StoredAnswer {
  if (typeof answer === "object") return { applicability: "assessed", score: answer.score, rationale: null };
  const u = UNSCORED.find((x) => x.id === answer)!;
  return { applicability: u.applicability, score: null, rationale: u.rationale };
}

/** What a stored response means; null while the question is unanswered. */
export function answerOf(stored: StoredAnswer | null | undefined): SnapshotAnswer | null {
  if (!stored) return null;
  if (stored.applicability === "assessed") return stored.score === null ? null : { score: stored.score };
  if (stored.applicability === "not_assessed") return "not_known";
  if (stored.applicability === "not_applicable") {
    return stored.rationale === "No relevant opportunity" ? "no_opportunity" : "not_applicable";
  }
  return null;
}

/**
 * The examples shown below a question (v2.1.0): how_tested's "- " lines. None
 * for v2.0.0, whose how_tested is the respondent instruction the introduction
 * already gives.
 */
export function examplesOf(howTested: string | null | undefined): string[] {
  return (howTested ?? "")
    .split("\n")
    .filter((l) => l.startsWith("- "))
    .map((l) => l.slice(2).trim());
}

/** The 28 days ending on the day the run was created, as dates to show. */
export function observationWindow(runCreatedAt: string): { from: string; to: string } {
  const to = new Date(runCreatedAt);
  const from = new Date(to.getTime() - 27 * 24 * 60 * 60 * 1000);
  const fmt = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  return { from: fmt(from), to: fmt(to) };
}

export interface SnapshotItem {
  key: string;        // the question's criterion key
  sequence: number;   // catalogue order
  area: string;
  question: string;
}

export interface SnapshotRow extends SnapshotItem {
  answer: string;              // what the respondent chose, in words
  score: number | null;        // null when unscored or unanswered
  unscoredReason: string | null;
}

export interface SnapshotReport {
  rows: SnapshotRow[];
  scored: number;
  total: number;
  unscored: SnapshotRow[];
  /** Null when the profile is partial (fewer than MIN_SCORED_TO_RANK scored). */
  priorities: SnapshotRow[] | null;
}

/**
 * The short report: every question with its answer, the coverage, and up to
 * three areas to investigate — the lowest scores, ties in catalogue order. An
 * answer at the top of the scale ("Consistently") is never an area to
 * investigate.
 */
export function snapshotReport(
  items: readonly SnapshotItem[],
  answers: ReadonlyMap<string, StoredAnswer>,
  levels: readonly { value: number; state: string }[],
): SnapshotReport {
  const top = Math.max(...levels.map((l) => l.value));
  const rows: SnapshotRow[] = [...items]
    .sort((a, b) => a.sequence - b.sequence)
    .map((item) => {
      const a = answerOf(answers.get(item.key));
      if (a === null) return { ...item, answer: "Not answered", score: null, unscoredReason: "Not answered" };
      if (typeof a === "object") {
        const state = levels.find((l) => l.value === a.score)?.state ?? String(a.score);
        return { ...item, answer: state, score: a.score, unscoredReason: null };
      }
      const label = UNSCORED.find((u) => u.id === a)!.label;
      return { ...item, answer: label, score: null, unscoredReason: label };
    });
  const scoredRows = rows.filter((r) => r.score !== null);
  const priorities =
    scoredRows.length < MIN_SCORED_TO_RANK
      ? null
      : scoredRows
          .filter((r) => r.score! < top)
          .sort((a, b) => a.score! - b.score! || a.sequence - b.sequence)
          .slice(0, MAX_PRIORITIES);
  return {
    rows,
    scored: scoredRows.length,
    total: rows.length,
    unscored: rows.filter((r) => r.score === null),
    priorities,
  };
}

/**
 * What the Snapshot indicates (slice 2, DESIGN_SLICE_SNAPSHOT_SLICE_2 011126
 * V1.0.md): one paragraph chosen by how the scored answers spread, counted,
 * never averaged. Null for a partial profile. Scores 1-2 are Never/Rarely,
 * 4-5 Usually/Consistently.
 */
export function snapshotIndication(m: SnapshotReport): string | null {
  if (m.priorities === null) return null;
  const scores = m.rows.map((r) => r.score).filter((s): s is number => s !== null);
  const low = scores.filter((s) => s <= 2).length;
  const high = scores.filter((s) => s >= 4).length;
  const never = scores.filter((s) => s === 1).length;
  if (low * 3 >= scores.length) {
    const most = high === 0 ? "and none happened usually or consistently" : `and only ${high} happened usually or consistently`;
    return (
      "The answers point to a transformation under strain, with many concerns and considerable improvement potential: " +
      `${low} of the ${scores.length} practices happened rarely or never in the four weeks, ${most}.`
    );
  }
  if (high * 3 >= scores.length * 2 && never === 0) {
    return m.priorities.length > 0
      ? "The answers point to practices that mostly work; the areas below are where to look first."
      : "The answers point to practices that mostly work.";
  }
  return "The answers show a mixed picture: some practices hold, others happen only sometimes.";
}

/** The Harvey ball for one answer: its share of the 1-5 scale, or the unscored state. */
export function ballFor(row: SnapshotRow): { share: number | null; state: "share" | "incomplete" | "not_applicable" } {
  if (row.score !== null) return { share: shareOfScale(row.score, { min: 1, max: 5 }), state: "share" };
  const notApplicable = row.unscoredReason === "Not applicable" || row.unscoredReason === UNSCORED[2].label;
  return { share: null, state: notApplicable ? "not_applicable" : "incomplete" };
}
